// React-query hooks. ALL data fetching in the app should funnel through here.

import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { api, buildFilterQuery } from "./client";
import type { FilterEntry } from "../state/filterStore";
import type {
  HealthResponse,
  PageDataResponse,
  PagesIndex,
} from "../types/pbi";

/** GET /api/health — pinged every 30 s by the top bar. */
export function useHealth(): UseQueryResult<HealthResponse> {
  return useQuery({
    queryKey: ["health"],
    queryFn: ({ signal }) => api.get<HealthResponse>("/health", signal),
    refetchInterval: 30_000,
    refetchIntervalInBackground: true,
    staleTime: 25_000,
    retry: 1,
  });
}

/** GET /api/pages — the page index (mirrors webapp/specs/pages.json). */
export function usePagesIndex(): UseQueryResult<PagesIndex> {
  return useQuery({
    queryKey: ["pages"],
    queryFn: ({ signal }) => api.get<PagesIndex>("/pages", signal),
    staleTime: 5 * 60_000,
  });
}

/** GET /api/pages/{folder}/data — spec + data per visual on a page.
 *
 * P0: `filters` is threaded into both the queryKey AND the URL so that a
 * cross-filter click on any visual (which updates the Zustand store) actually
 * causes react-query to re-fetch the page with the new filter set. Without
 * the filters in the key, react-query short-circuits to the cached unfiltered
 * payload and the dashboard "feels static". staleTime dropped to 10s so
 * filter-driven refetches are not aggressively coalesced. */
export function usePage(
  folder: string | undefined,
  filters?: Record<string, FilterEntry>,
): UseQueryResult<PageDataResponse> {
  return useQuery({
    queryKey: ["page", folder, filters],
    enabled: !!folder,
    queryFn: ({ signal }) =>
      api.get<PageDataResponse>(
        `/pages/${folder}/data${buildFilterQuery(filters)}`,
        signal,
      ),
    staleTime: 10_000,
  });
}

/** GET /api/tables/{table} — raw table rows. */
export function useTable<TRow = Record<string, unknown>>(
  table: string | undefined,
): UseQueryResult<{ rows: TRow[]; columns: string[] }> {
  return useQuery({
    queryKey: ["table", table],
    enabled: !!table,
    queryFn: ({ signal }) =>
      api.get<{ rows: TRow[]; columns: string[] }>(`/tables/${table}`, signal),
    staleTime: 60_000,
  });
}

/** GET /api/bookmarks — list of bookmark groups + flat by-name lookup. */
// P2-bookmarks: each bookmark may now carry a flat `filters` array. The
// shape mirrors what the backend extracts from the bookmark JSON's
// explorationState (report-level + per-section). Frontend ingests this
// into the Zustand filter store on click.
export interface BookmarkFilter {
  table: string;
  column: string;
  op: string;
  value: unknown;
}
export interface BookmarkChild {
  name: string;
  displayName: string;
  activeSection?: string | null;
  filters?: BookmarkFilter[];
}
export interface BookmarkGroup {
  name: string;
  displayName: string;
  children: BookmarkChild[];
  activeSection?: string | null;
  filters?: BookmarkFilter[];
}
export interface BookmarksResponse {
  items: BookmarkGroup[];
  byName: Record<string, BookmarkChild>;
}
export function useBookmarks(): UseQueryResult<BookmarksResponse> {
  return useQuery({
    queryKey: ["bookmarks"],
    queryFn: ({ signal }) => api.get<BookmarksResponse>("/bookmarks", signal),
    staleTime: 5 * 60_000,
  });
}

/** GET /api/measures/{name} — evaluate one DAX measure with current filters. */
export function useMeasure(
  name: string | undefined,
  filters?: Record<string, unknown>,
): UseQueryResult<{ value: string | number | null; formatted?: string }> {
  return useQuery({
    queryKey: ["measure", name, filters],
    enabled: !!name,
    queryFn: ({ signal }) =>
      api.post<{ value: string | number | null; formatted?: string }>(
        `/measures/${name}`,
        filters ?? {},
        signal,
      ),
    staleTime: 30_000,
  });
}

// P2-bookmarks: per-name bookmark fetch. Mostly for callers who only have a
// bookmark name and don't want to materialize the full /api/bookmarks list.
// The list endpoint also surfaces filters now, so most navigators can avoid
// this hop entirely — this exists for ActionButton (Bookmark target) and any
// future single-bookmark consumer.
export function useBookmark(
  name: string | undefined,
): UseQueryResult<BookmarkChild> {
  return useQuery({
    queryKey: ["bookmark", name],
    enabled: !!name,
    queryFn: ({ signal }) =>
      api.get<BookmarkChild>(`/bookmarks/${name}`, signal),
    staleTime: 5 * 60_000,
  });
}
